import { useLocation } from 'react-router-dom';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import theme from '@src/styles/theme';
import Typography from '@src/new-components/Typography';
import DropdownCustom from '@src/new-components/Dropdown';
import {
  REQUEST_STATUS_OPTIONS,
  REQUEST_STATUS,
  SERVICE_TYPE,
  STT_REQUEST_STATUS_MAP,
  STT_REQUEST_STATUS,
  STT_REQUEST_STATUS_OPTIONS,
} from '@src/constants/apiApp';
import { FETCH_REQUESTS_INTERVAL } from '@src/constants/websocket';
import { COLOR } from '@src/styles/color';
import apis from '@src/apis';
import RequestTable from './RequestTable';
import {
  StyledRequestsList,
  StyledRequestsListHeader,
  StyledRequestsListContainer,
} from './index.style';

const DEFAULT_ROW_PER_PAGE = 5;

const RequestsList = ({
  startDate,
  endDate,
  statusRequest,
  setStatusRequest,
  activeServiceTab,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const appId = location.pathname.split('/')[2];
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROW_PER_PAGE);
  const [total, setTotal] = useState(0);

  const requestsRef = useRef(requests);
  const abortControllerRef = useRef(null);

  const hasInProgressRequest = useMemo(
    () =>
      requestsRef.current.some(
        (request) => request.status === REQUEST_STATUS.IN_PROGRESS,
      ),
    [requestsRef.current],
  );

  const handleChangeStatus = (value) => {
    setStatusRequest(value);
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const cancelPendingRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const fetchSTTRequests = async () => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const data = await apis.stt.getSTTTranscripts({
        appId,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        startDate,
        endDate,
        status:
          statusRequest === STT_REQUEST_STATUS.ALL ? undefined : statusRequest,
        signal: controller.signal,
      });

      if (data?.requests) {
        const mapped = data.requests.map((item) => {
          const callbackStatusCode = item.clientSttCallbackStatusCode;
          const hasCallbackError =
            callbackStatusCode >= 400 && callbackStatusCode < 600;
          return {
            id: item.transcriptId || item.sessionId || item.id,
            createdAt: item.createdAt,
            credits: item?.billingCredits || item?.billingPrepaidCredits,
            status: STT_REQUEST_STATUS_MAP[item.status] || item.status,
            audioDurationSeconds:
              item.audioDurationSeconds || item?.totalAudioSeconds,
            billedSeconds: item.billedSeconds,
            hasWarning: hasCallbackError,
            callbackStatusCode: hasCallbackError
              ? callbackStatusCode
              : undefined,
            callbackAt: hasCallbackError ? item.clientSttCallbackAt : undefined,
          };
        });
        setRequests(mapped);
        setTotal(data.metadata.total);
      }
      setLoading(false);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        setLoading(false);
      }
    }
  };

  const fetchTTSRequests = async () => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const data = await apis.apps.getRequests({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort: 'createdAt_desc',
        appId,
        fields:
          '_id,title,characters,credits,createdAt,status,voice,app,audioLink,retentionPeriod,stt,seconds,audioType,processingAt,endedAt',
        startDate,
        endDate,
        status:
          statusRequest === REQUEST_STATUS.ALL ? undefined : statusRequest,
        signal: controller.signal,
      });

      if (data?.status) {
        setRequests(data.result.requests);
        setTotal(data.result.metadata.total);
      }
      setLoading(false);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        setLoading(false);
      }
    }
  };

  const fetchAppRequests =
    activeServiceTab === SERVICE_TYPE.STT ? fetchSTTRequests : fetchTTSRequests;

  const fetchProgressRequest = async (requestId) => {
    const data = await apis.requests.getProgressRequest(requestId);
    if (data && data.status) {
      const newRequests = requestsRef.current.map((request) => {
        if (request.id === requestId) {
          const { progress, status, audioLink, endedAt, processingAt } =
            data.result;
          return {
            ...request,
            progress,
            status,
            audioLink,
            processingAt: processingAt || request.processingAt,
            endedAt: endedAt || request.endedAt,
          };
        }
        return request;
      });
      setRequests(newRequests);
    }
  };

  const updateInprogressRequest = (requestIds) => {
    Promise.all(
      requestIds.map(async (requestId) => {
        await fetchProgressRequest(requestId);
        return null;
      }),
    );
  };

  useEffect(() => {
    fetchAppRequests();
  }, [statusRequest, page, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    if (page === 0) {
      fetchAppRequests();
    } else {
      setPage(0);
    }
  }, [activeServiceTab]);

  useEffect(() => {
    requestsRef.current = requests;
  }, [requests]);

  useEffect(() => {
    const fetchRequestsInterval = setInterval(async () => {
      const inProgressRequest = requestsRef.current
        .filter((request) => request.status === REQUEST_STATUS.IN_PROGRESS)
        .map((request) => request.id);
      if (inProgressRequest?.length) updateInprogressRequest(inProgressRequest);
    }, FETCH_REQUESTS_INTERVAL);

    return () => clearInterval(fetchRequestsInterval);
  }, [hasInProgressRequest]);

  return (
    <StyledRequestsList>
      <StyledRequestsListContainer>
        <StyledRequestsListHeader>
          <Typography className="title-request" variant="body-20-bold">
            {t('historyRequest')}
          </Typography>

          <div className="filter-item">
            <DropdownCustom
              width={isMobile && '100%'}
              variant="api-request-status"
              options={Object.values(
                activeServiceTab === SERVICE_TYPE.STT
                  ? STT_REQUEST_STATUS_OPTIONS
                  : REQUEST_STATUS_OPTIONS,
              ).map((item) => ({
                label: (
                  <Typography
                    variant="body-14-regular"
                    color={COLOR.label2[100]}
                  >
                    {t(item.label)}
                  </Typography>
                ),
                value: item.value,
              }))}
              onChange={handleChangeStatus}
              title={
                <Typography variant="body-14-regular" color={COLOR.label2[100]}>
                  {t(
                    activeServiceTab === SERVICE_TYPE.STT
                      ? STT_REQUEST_STATUS_OPTIONS[statusRequest]?.label
                      : REQUEST_STATUS_OPTIONS[statusRequest]?.label,
                  ) || t('allStatus')}
                </Typography>
              }
              value={statusRequest}
            />
          </div>
        </StyledRequestsListHeader>
      </StyledRequestsListContainer>
      <RequestTable
        requests={requests}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        total={total}
        loading={loading}
        activeServiceTab={activeServiceTab}
        onRefresh={fetchAppRequests}
      />
    </StyledRequestsList>
  );
};

export default RequestsList;
